import type { ComponentType, ReactNode } from 'react';
import { Fragment, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Bootstrap, e2eLogin, Layout, preloadGlobalSpinner, Toaster } from '@eo/shared';

import './styles/app.css';
import { store } from './app/store';
import { bootstrap } from './app/bootstrap';
import { sidebarNavigation } from './app/sidebarNavigation';
import { Routes as AppRoutes } from './app/Routes';
import { AuthProvider } from './auth/AuthProvider';
import { userManager } from './auth/userManager';
import { AuthCallbackError } from './components/AuthCallbackError';
import { BootSplash } from './components/BootSplash';
import { UserMenu } from './components/UserMenu';
import { StaffProvider } from './staff/StaffProvider';
import { initTheme } from './theme';

initTheme();
// The splash renders across a hard OIDC redirect; a cold Lottie chunk would
// show its empty placeholder instead of the mark.
preloadGlobalSpinner();

const rootEl = document.getElementById('root')!;
const path = window.location.pathname;
const isE2E = import.meta.env.VITE_E2E === 'true';
const isAuthCallbackPath = path === '/auth/callback' || path === '/auth/silent-callback';

function isValidE2EAuthData(value: unknown): value is {
  email: string;
  token: string;
  transactionId: string;
} {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.email === 'string' &&
    typeof v.token === 'string' &&
    typeof v.transactionId === 'string'
  );
}

// E2E bridge: the Playwright fixture drives the app via window.__REDUX_STORE__.
// Skipped on /auth/* so a misconfigured E2E build can't silently bypass the
// OIDC handlers.
if (isE2E && !isAuthCallbackPath) {
  const w = window as unknown as { __REDUX_STORE__: typeof store };
  w.__REDUX_STORE__ = store;

  // Restore prior e2e auth so flows that trigger a full reload keep their
  // session across the reboot. Validate the shape — a malformed entry (manual
  // edit, version skew) is dropped so it doesn't pollute every subsequent boot.
  const stored = localStorage.getItem('e2e_auth_data');
  if (stored) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(stored);
    } catch {
      parsed = null;
    }
    if (isValidE2EAuthData(parsed)) {
      store.dispatch(e2eLogin(parsed));
    } else {
      console.error('[e2e] dropping malformed e2e_auth_data from localStorage');
      localStorage.removeItem('e2e_auth_data');
    }
  }
}

// In E2E the OIDC dance never runs — the fixture dispatches e2e/login instead,
// so the tree mounts without AuthProvider (which would signinRedirect on boot).
const AuthBoundary: ComponentType<{ children: ReactNode }> = isE2E ? Fragment : AuthProvider;

// Short-circuit OIDC redirect handlers before mounting the full tree — both
// are URL-only handoffs to oidc-client-ts; mounting the shell here would race
// the boot gate against the code exchange it depends on.
if (!isE2E && path === '/auth/silent-callback') {
  userManager.signinSilentCallback().catch((err) => {
    console.error('[silent-callback] silent renew failed:', err);
  });
  // hidden iframe — no UI; oidc-client-ts only needs the URL hash parsed.
  rootEl.textContent = '';
} else if (!isE2E && path === '/auth/callback') {
  // Inline error UI on code-exchange failure: a redirect would re-mount
  // AuthProvider and re-issue signinRedirect, looping silently.
  const root = createRoot(rootEl);
  root.render(<BootSplash />);
  void (async () => {
    try {
      const user = await userManager.signinRedirectCallback();
      const returnTo = (user.state as { returnTo?: string } | undefined)?.returnTo;
      window.location.replace(returnTo && returnTo.startsWith('/') ? returnTo : '/');
    } catch (err) {
      console.error('[auth-callback] code exchange failed:', err);
      root.render(
        <AuthCallbackError
          onRetry={() => {
            userManager.signinRedirect().catch((retryErr) => {
              console.error('[auth-callback] retry signinRedirect failed:', retryErr);
            });
          }}
        />,
      );
    }
  })();
} else {
  // Mission Control composes the shell from AppShell's exported pieces instead
  // of <AppShell> itself: StaffProvider boots off /api/v1/cfo/me against the
  // staff registry — the app has no tenant concept.
  createRoot(rootEl).render(
    <StrictMode>
      <Provider store={store}>
        <Bootstrap bootstrapFunc={bootstrap} splash={<BootSplash />}>
          <BrowserRouter>
            <AuthBoundary>
              <StaffProvider splash={<BootSplash />}>
                <Layout
                  appTitle="E+O Mission Control"
                  sidebarNavigation={sidebarNavigation}
                  sidebarFooter={({ collapsed }) => <UserMenu collapsed={collapsed} />}
                >
                  <AppRoutes />
                  <Toaster />
                </Layout>
              </StaffProvider>
            </AuthBoundary>
          </BrowserRouter>
        </Bootstrap>
      </Provider>
    </StrictMode>,
  );
}
